import { useMemo, useState } from "react";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { UserRole } from "../../app/userSlice";
import ModalFrame from "../../components/ModalFrame";
import Tabs, { SubTab } from "../../components/Tabs";
import AddUserForm from "../users/editUserByAdmin/AddUserForm";
import AddExistingSchoolAdminForm from "./AddExistingSchoolAdminForm";

export enum AddSchoolAdminSubTabs {
	Existing = "Existing School Administrator",
	New = "New School Administrator",
}

interface AddSchoolAdminProps {
	schoolId: number;
	districtId?: number;
	setErrorMessage?: (message: string) => void;
}

export default function AddSchoolAdminButton(props: AddSchoolAdminProps) {
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
	const [currentTabName, setCurrentTabName] = useState(AddSchoolAdminSubTabs.Existing);

	const initialSubTabs: SubTab[] = useMemo(
		() => [
			{
				name: AddSchoolAdminSubTabs.Existing,
				current: currentTabName === AddSchoolAdminSubTabs.Existing,
				content: (
					<AddExistingSchoolAdminForm
						schoolId={props.schoolId}
						onClose={handleCloseForm}
						onSwitchToNewTab={!hasAutoSwitched ? handleAutoSwitchTabs : undefined}
					/>
				),
			},
			{
				name: AddSchoolAdminSubTabs.New,
				current: currentTabName === AddSchoolAdminSubTabs.New,
				content: (
					<AddUserForm
						selectedRole={UserRole.SchoolAdmin}
						saveButtonText="Add School Administrator"
						onClose={handleCloseForm}
						hideDistrictSchool={true}
						schoolId={props.schoolId}
						districtId={props.districtId}
					/>
				),
			},
		],
		[props, currentTabName, hasAutoSwitched]
	);

	function handleOpenForm() {
		setIsFormOpen(true);
		setCurrentTabName(AddSchoolAdminSubTabs.Existing);
		setHasAutoSwitched(false);
	}

	function handleCloseForm() {
		setIsFormOpen(false);
	}

	function handleAutoSwitchTabs() {
		setHasAutoSwitched(true);
		setCurrentTabName(AddSchoolAdminSubTabs.New);
	}

	return (
		<>
			<Button
				id="add-school-admin-button"
				onClick={handleOpenForm}
				className="btn btn-primary">
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add
				</span>
			</Button>

			<ModalFrame
				open={isFormOpen}
				title="Add Administrator to School"
				onClose={handleCloseForm}
				maxWidth="max-w-[750px]"
				overflow="overflow-visible">
				<Tabs
					initialSubTabs={initialSubTabs}
					ariaLabel="Add School Administrator Tabs"
					containerClass="mt-1.5"
					tabPadding="p-2"
					forceActiveTabName={hasAutoSwitched ? currentTabName : undefined}
				/>
			</ModalFrame>
		</>
	);
}
